/**
 * PricingPage - Public pricing with 3 tiers and Stripe Checkout
 * MODULE: core/auth/frontend/pages/PricingPage.tsx
 */
import { useState } from 'react';
import { Card, Button, TextInput, Label, Spinner } from 'flowbite-react';
import { HiCheck } from 'react-icons/hi';
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import { useAuth } from '../hooks/useAuth';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth/subscription`;
const FRONTEND_URL = typeof window !== 'undefined' ? window.location.origin : '';

const PLANS = [
  { tier: 'starter', name: 'Starter', priceMonthly: 29, priceYearly: 290, features: ['Up to 5 users', 'Basic analytics', 'Email support'] },
  { tier: 'professional', name: 'Professional', priceMonthly: 99, priceYearly: 990, popular: true, features: ['Up to 25 users', 'Advanced analytics', 'Priority support', 'API access'] },
  { tier: 'enterprise', name: 'Enterprise', priceMonthly: 299, priceYearly: 2990, features: ['Unlimited users', 'Custom integrations', 'Dedicated support', 'SLA'] },
];

export function PricingPage() {
  const { user, isAuthenticated } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGetStarted = async (tier: string) => {
    const submitEmail = email?.trim() || (user as { email?: string })?.email;
    if (!submitEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(submitEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError(null);
    setLoadingTier(tier);
    try {
      const tenantIdRaw = isAuthenticated ? localStorage.getItem('tenant_id') : null;
      const tenantId = tenantIdRaw?.startsWith('tnt_')
        ? tenantIdRaw.replace(/^tnt_/, '')
        : tenantIdRaw || undefined;
      const res = await fetch(`${API_BASE}/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_tier: tier,
          email: submitEmail,
          success_url: `${FRONTEND_URL}/payment-confirm?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${FRONTEND_URL}/pricing`,
          ...(tenantId && { tenant_id: tenantId }),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Checkout failed');
      if (data.url) window.location.href = data.url;
      else throw new Error('No checkout URL');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed');
      setLoadingTier(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Simple, transparent pricing</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Choose the plan that fits your team.</p>
        </div>

        <div className="flex justify-center gap-2 mb-8">
          <button
            type="button"
            onClick={() => setBillingInterval('monthly')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${billingInterval === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'}`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingInterval('annual')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${billingInterval === 'annual' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'}`}
          >
            Annual
            <span className="ml-1 text-xs bg-green-500 text-white px-1.5 py-0.5 rounded">Save 2 months</span>
          </button>
        </div>

        {!isAuthenticated && (
          <div className="max-w-sm mx-auto mb-8">
            <Label htmlFor="pricing-email">Email to get started</Label>
            <TextInput
              id="pricing-email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1"
            />
            {error && <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {PLANS.map((plan) => (
            <Card
              key={plan.tier}
              className={plan.popular ? 'ring-2 ring-blue-500 relative' : ''}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Most Popular
                </div>
              )}
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{plan.name}</h3>
              <div className="mt-2">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  ${billingInterval === 'monthly' ? plan.priceMonthly : Math.round(plan.priceYearly / 12)}
                </span>
                <span className="text-gray-600 dark:text-gray-400">/month</span>
              </div>
              <ul className="space-y-2 mt-4">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <HiCheck className="text-green-500 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                color={plan.popular ? 'blue' : 'gray'}
                className="mt-6 w-full"
                disabled={!!loadingTier}
                onClick={() => handleGetStarted(plan.tier)}
              >
                {loadingTier === plan.tier ? <><Spinner size="sm" className="mr-2" /> Processing...</> : 'Get Started'}
              </Button>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
